/**
 * Resolve a imagem de compartilhamento (`og:image`) de um conteúdo.
 *
 * Não existe um campo único de imagem no portal, então a busca percorre uma
 * cadeia — do mais explícito ao mais genérico. A ordem veio de medição no
 * banco real, não de suposição: as notícias usam `image` (40 de 40 no banco
 * mínimo), nenhum conteúdo usa `preview_image_link` hoje, e a home não tem
 * imagem própria.
 */

import { flattenToAppURL, toPublicURL } from '@plone/volto/helpers/Url/Url';

import { singleLine } from './text';

export type OgImageSource =
  | 'opengraph_image'
  | 'image'
  | 'preview_image_link'
  | 'block'
  | 'fallback';

export interface OgImage {
  url: string;
  width?: number;
  height?: number;
  alt: string;
  /** Qual elo da cadeia respondeu. Existe para os testes e para depuração. */
  source: OgImageSource;
}

interface Scale {
  download?: string;
  width?: number;
  height?: number;
}

/**
 * `flattenToAppURL` só remove a origem da API. Um asset servido pelo bundler
 * (a imagem institucional em desenvolvimento sai em `localhost:3001`) continua
 * absoluto, e concatenar a origem pública nele produziria
 * `https://site/https://bundler/arquivo.png`.
 */
const toPublic = (url: string): string => {
  const flattened = flattenToAppURL(url);
  return /^https?:\/\//i.test(flattened) ? flattened : toPublicURL(flattened);
};

/** Prefere a escala `large`; cai no arquivo original quando ela não existe. */
const pickScale = (field: any): Scale | null => {
  if (!field) return null;
  const large = field.scales?.large;
  if (large?.download) return large;
  if (field.download) {
    return {
      download: field.download,
      width: field.width,
      height: field.height,
    };
  }
  return null;
};

/** A URL das escalas do resumo é relativa ao objeto que as carrega. */
const scaleOfSummary = (summary: any): Scale | null => {
  const entry = summary?.image_scales?.[summary?.image_field ?? 'image']?.[0];
  if (!entry) return null;
  const file = entry.scales?.large?.download || entry.download;
  if (!file) return null;
  const scale = entry.scales?.large ?? entry;
  return { download: file, width: scale.width, height: scale.height };
};

const firstImageBlock = (content: any): any => {
  const order: string[] = content?.blocks_layout?.items ?? [];
  const blocks = content?.blocks ?? {};
  return order
    .map((id) => blocks[id])
    .find(
      (block) =>
        block?.['@type'] === 'image' && (block.url || block.image_scales),
    );
};

/**
 * `descricaoImagem` é o campo que a redação já preenche nas notícias (behavior
 * `noticia_meta`) e é o mesmo texto usado no `alt` da foto na matéria.
 */
const resolveAlt = (content: any, block: any, relationText?: string): string =>
  singleLine(content?.descricaoImagem) ||
  singleLine(content?.image_caption) ||
  singleLine(block?.alt) ||
  singleLine(relationText) ||
  singleLine(content?.title);

const build = (
  scale: Scale,
  source: OgImageSource,
  alt: string,
  baseUrl?: string,
): OgImage => ({
  url: toPublic(
    baseUrl ? `${baseUrl}/${scale.download}` : (scale.download as string),
  ),
  width: scale.width,
  height: scale.height,
  alt,
  source,
});

export const resolveOgImage = (
  content: any,
  fallbackUrl?: string,
): OgImage | null => {
  const institutionalImage = (alt: string): OgImage | null =>
    fallbackUrl
      ? {
          url: toPublic(fallbackUrl),
          width: 1200,
          height: 630,
          alt,
          source: 'fallback',
        }
      : null;

  if (!content) return institutionalImage('');

  const block = firstImageBlock(content);
  const alt = resolveAlt(content, block);

  // Sobrescrita explícita do editor (behavior `kitconcept.seo`): vence sempre.
  const explicit = pickScale(content.opengraph_image);
  if (explicit) return build(explicit, 'opengraph_image', alt);

  // Leadimage — o caso real das notícias. Mesma ordem do `NewsView`, para que
  // o card e a matéria mostrem a mesma foto.
  const lead = pickScale(content.image);
  if (lead) return build(lead, 'image', alt);

  const relation = content.preview_image_link;
  const relationScale = scaleOfSummary(relation);
  if (relationScale && relation?.['@id']) {
    const relationAlt = resolveAlt(
      content,
      block,
      relation.description || relation.title,
    );
    return build(
      relationScale,
      'preview_image_link',
      relationAlt,
      relation['@id'],
    );
  }

  if (block?.url) {
    const blockScale = scaleOfSummary(block);
    return blockScale
      ? build(blockScale, 'block', alt, block.url)
      : build({ download: '@@images/image/large' }, 'block', alt, block.url);
  }

  return institutionalImage(alt);
};

export default resolveOgImage;
